// WhatWeVerify.js
import React from 'react';
import { motion } from 'framer-motion';
import { User, Briefcase, MapPin, DollarSign, Building } from 'lucide-react';
import VerificationProcess from '../components/VerificationProcess';
import VerificationCoverage from '../components/VerificationCoverage';
import VerificationDetails from '../components/VerificationDetails';

const WhatWeVerify = () => {
  const verificationTypes = [
    {
      id: 1,
      icon: User,
      title: "Identity & Background",
      items: [
        "Government ID inspection (image quality, data consistency)",
        "Guarantor / referee calls",
        "Manual PEP/sanctions & adverse media screening"
      ]
    },
    {
      id: 2,
      icon: Briefcase,
      title: "Employment & Education",
      items: [
        "Direct confirmation with HR desks and registrars (titles, dates, award/degree)",
        "Professional memberships and licenses",
        "Structured reference interviews (with consent)"
      ]
    },
    {
      id: 3,
      icon: MapPin,
      title: "Address Verification",
      items: [
        "In-person visit (up to 2 attempts) by a vetted field agent",
        "Photos, geo-coordinates, and timestamps (where permitted)",
        "Neighbor/landlord corroboration and occupancy status"
      ]
    },
    {
      id: 4,
      icon: DollarSign,
      title: "Financial Standing (consent-based)",
      items: [
        "Bank reference letters & account ownership confirmations",
        "Proof-of-income (employment letters, pay slips)",
        "Asset evidence (e.g., property or vehicle documentation)"
      ]
    },
    {
      id: 5,
      icon: Building,
      title: "Corporate & Guarantor Due Diligence",
      items: [
        "Company registration lookups via official registries",
        "Director/ownership confirmation (where records are available)",
        "Guarantor identity and capacity checks"
      ]
    }
  ];

  return (
    <section className="py-16 px-6 bg-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div 
          className="text-left mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-inter font-bold text-acBlack">
            What we verify
          </h2>
        </motion.div>

        {/* Verification Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {verificationTypes.map((type, index) => {
            const IconComponent = type.icon;
            return (
              <motion.div
                key={type.id}
                className="text-left"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                {/* Icon */}
                <div className="flex items-start mb-6">
                  <div className="w-16 h-16 bg-acYellow rounded-full flex items-center justify-center flex-shrink-0">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-inter font-bold text-acBlack mb-4">
                  {type.title}
                </h3>
                <ul className="space-y-2">
                  {type.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="text-gray-600 leading-relaxed text-sm flex items-start">
                      <span className="mr-2 text-acYellow font-bold">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
      {/* How Our Verification Works */}
<VerificationProcess />

{/* Coverage & Timelines */}
<VerificationCoverage />

{/* Report Details, Use Cases, and Assurance */}
<VerificationDetails />
    </section>
  );
};

export default WhatWeVerify;
